import React, { useState, useEffect } from 'react';
import { Table, message } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ICommonCode, IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import {
  AddSquare,
  ArrowDown2,
  ArrowUp2,
  Edit2,
  PlayCricle,
  Trash,
} from 'iconsax-react';
import SelectMultiple from '../_component/SearchMultiple';

import { characterApis } from 'apis/resource/characterApis';
import { liveBackgroundApi } from 'apis/resource/liveBackgroundApis';
import CreateModal from '../_component/Live/CreateModal';
import DeleteModal from 'components/modal/DeleteModal';
import UploadModal from '../_component/Live/UploadModal';
import CheckboxStyled from 'components/CheckboxStyled';

/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const LiveBackgroundTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string[]>([]); // 검색어
  const [visible, setVisible] = useState({
    register: false,
    delete: false,
    zip: false,
    name: '',
  }); // 모달 show on/off
  const [selectedData, setSelectedData] = useState<any>(null);

  const [charactersData, setCharacterData] = useState<ICommonCode[] | null>(
    null,
  );
  const [selectCharacterData, setSelectCharacterData] = useState<
    ICommonCode[] | null
  >(null);

  useEffect(() => {
    if (charactersData) {
      setSelectCharacterData(charactersData);
    }
  }, [charactersData]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await liveBackgroundApi.liveBackgroundList(projectId);
        setData(res.list);
        const characterRes = await characterApis.list(projectId.toString());
        setCharacterData(characterRes);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.live_name,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        await liveBackgroundApi.liveBackgroundDelete(
          projectId,
          selectedData.id,
        );
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: selectedData?.live_name,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const handleLink = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
        project_type: location?.state?.record?.project_type,
      },
    });
  };

  const expandedRowRender = (record) => {
    return (
      <MainTable
        columns={columnsNested}
        dataSource={record.details}
        isPagination={false}
      />
    );
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <AddSquare
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, zip: true });
            }}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <PlayCricle
            size='24'
            onClick={() => {
              return;
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 3,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              handleLink(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 4,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.name',
      ),
      dataIndex: 'live_name',
      sorter: (a, b) => {
        if (a.live_name && b.live_name) {
          return a.live_name.localeCompare(b.live_name, undefined, {
            sensitivity: 'base',
          });
        } else if (a.live_name && !b.live_name) {
          return -1;
        } else if (!a.live_name && b.live_name) {
          return 1;
        } else {
          return 0;
        }
      },
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.character',
      ),
      dataIndex: 'speaker',
      sorter: (a, b) => {
        if (a.speaker && b.speaker) {
          return a.speaker.localeCompare(b.speaker, undefined, {
            sensitivity: 'base',
          });
        } else if (a.speaker && !b.speaker) {
          return -1;
        } else if (!a.speaker && b.speaker) {
          return 1;
        } else {
          return 0;
        }
      },
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.version',
      ),
      dataIndex: 'resource_ver',
      width: '7rem',
    },
    {
      title: 'X Offset',
      dataIndex: 'offset_x',
      width: '7rem',
    },
    {
      title: 'Y Offset',
      dataIndex: 'offset_y',
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.scale',
      ),
      dataIndex: 'game_scale',
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.status',
      ),
      dataIndex: 'is_public',
      width: '7rem',
      render: (_) => {
        return <CheckboxStyled isChecked={_} />;
      },
      sorter: (a, b) => {
        const statusA = a.is_public ? 1 : 0;
        const statusB = b.is_public ? 1 : 0;
        return statusA - statusB;
      },
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  const columnsNested = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.assetName',
      ),
      dataIndex: 'file_name',
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.motion',
      ),
      dataIndex: 'motion_name',
      width: '7rem',
    },
  ];

  // handle search input onChange
  const handleSelectChange = (value: any, data: any) => {
    setSearchData(value);
  };
  useEffect(() => {
    const cloneData = JSON.parse(JSON.stringify(data));
    if (cloneData) {
      if (searchData.length) {
        const newArray = cloneData.filter((obj) =>
          searchData.includes(obj.speaker),
        );
        setTableData(newArray);
      } else {
        setTableData(data);
      }
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SelectMultiple
          optionList={selectCharacterData?.map((e) => {
            return {
              value: e.code,
              label: e.code_name,
            };
          })}
          placeholder='Search...'
          mode='tags'
          onChange={handleSelectChange}
          width='320px'
          value={searchData}
          setValue={setSearchData}
        />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, register: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.createModal.title',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable
          rowClassName={(record) => {
            if (record && record.id) {
              if (expandedRows.includes(record.id)) {
                return 'active';
              }
            }
          }}
          isExpand
          rowKey='id'
          columns={columnsOrigin}
          dataSource={tableData}
          expandable={{
            expandedRowRender: (record) => {
              return expandedRowRender(record);
            },
            expandIcon: ({ expanded, onExpand, record }) => {
              return expanded ? (
                <ArrowUp2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <ArrowDown2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRows(expandedRows);
            },
          }}
        />
      </TableWrapper>

      <CreateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        type='live_bg'
        projectId={projectId}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.deleteModal.modalTitle',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.deleteModal.content'
        label={visible.name}
        handleDelete={handleDelete}
        loading={loading}
      />

      <UploadModal
        visible={visible}
        setVisible={(e) => {
          setVisible({ ...visible, zip: e });
        }}
        refresh={refresh}
        setRefresh={setRefresh}
        data={selectedData}
        type='live_bg'
        projectId={projectId}
      />
    </>
  );
};

export default LiveBackgroundTable;

const TableWrapper = styled.div`
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;
